import React from 'react';
import Papa from 'papaparse';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

const SAMPLE_TSV_COLUMNS = [
  'condition',
  'department',
  'category',
  'merchant_category',
  'primary_tag',
  'primary_name',
  'discount',
] as const;

const SAMPLE_TSV_ROWS = [
  { discount: 80 },
  { condition: 'new', discount: 90 },
  { condition: 'good', department: "Men's", discount: 70 },
  { condition: 'fair', department: "Women's", category: 'Shoes', discount: 60 },
  { condition: 'like_new', primary_tag: 'brand', primary_name: 'Acme', discount: 85 },
];

const handleDownloadSample = () => {
  const tsv = Papa.unparse(SAMPLE_TSV_ROWS, {
    delimiter: '\t',
    columns: SAMPLE_TSV_COLUMNS as unknown as string[],
  });
  const blob = new Blob([tsv], { type: 'text/tab-separated-values' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'condition_discounts_sample.tsv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ConditionDiscountsDocumentation = () => (
  <Paper sx={{ p: 2, mt: 2, backgroundColor: 'grey.50' }}>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      Guide
    </Typography>

    <Typography variant="body2" paragraph>
      Condition discount rules adjust resale prices based on item condition. Each rule specifies
      a condition (e.g., new, like new, good, fair) and a discount percentage. When an item
      in that condition matches a rule&apos;s criteria, Trove applies the discount to calculate
      its resale price.
    </Typography>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      How rules are matched
    </Typography>
    <Typography variant="body2" paragraph>
      Rules are evaluated in position order (0 first). The first rule that matches an item is used.
      More specific rules should be placed before general rules. For example, a rule for
      &quot;Kids&apos; shoes&quot; should come before a general &quot;all shoes&quot; rule.
      We recommend having a catch-all rule at the end (no filters) to ensure every item gets a price.
    </Typography>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      Why you need to Apply changes
    </Typography>
    <Typography variant="body2" paragraph>
      Pricing rules are applied to your entire catalog, which can contain thousands of items.
      Instead of recalculating prices after every edit, Trove lets you make multiple changes
      and then apply them all at once. This is faster and lets you review your rules before
      they take effect.
    </Typography>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      Status states
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2 }}>
      <li>
        <Typography variant="body2">
          <em>Unapplied</em>
          {' '}
          — You have made changes that are not yet reflected in your catalog prices.
          Click Apply when you are ready.
        </Typography>
      </li>
      <li>
        <Typography variant="body2">
          <em>Processing</em>
          {' '}
          — Trove is calculating prices for all items in your catalog. This may take a few minutes
          for large catalogs. You cannot make changes while processing is in progress.
        </Typography>
      </li>
      <li>
        <Typography variant="body2">
          <em>Processed</em>
          {' '}
          — All catalog items have been priced according to your rules.
          You can now make additional changes and apply them when ready.
        </Typography>
      </li>
    </Box>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      Discount values
    </Typography>
    <Typography variant="body2" paragraph>
      The discount is always a percentage of the item&apos;s base price. A value of 80 means
      the item is priced at 80% of its base price. Rules without a condition act as catch-all
      defaults for items in any condition.
    </Typography>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      Importing rules
    </Typography>
    <Typography variant="body2">
      You can bulk-import rules from a TSV file. Note that importing replaces all existing rules.
      {' '}
      <Box
        component="span"
        onClick={handleDownloadSample}
        sx={{
          color: 'primary.main',
          textDecoration: 'underline',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'none' },
        }}
      >
        Download a sample file
      </Box>
      {' '}
      to see the expected format.
    </Typography>
  </Paper>
);

export default ConditionDiscountsDocumentation;
